import { isApiError } from '@lib/api/utils/is-api-error';

/**
 * True for browser-native `fetch` transport failures that never produce an
 * HTTP response — connectivity drops, aborted navigations, DNS/TLS blips,
 * client timeouts, and similar client-lifecycle noise.
 *
 * Chrome/Chromium: `TypeError: Failed to fetch` (Sentry's fetch
 * instrumentation sometimes appends a parenthetical host, e.g.
 * `Failed to fetch (docs.services.example.com)`)
 * Chrome (stream body / mid-read): `TypeError: network error`
 * Safari / WebKit (incl. Chrome on iOS): `TypeError: Load failed` (sometimes
 * with a parenthetical host suffix)
 * Firefox: `TypeError: NetworkError when attempting to fetch resource.`
 *
 * Also matches `ApiError` with `status === 0` whose message is one of the
 * transport strings above (or `apiFetch`'s `'Network error'` / `'Request
 * timed out'` fallbacks). `apiFetch` in `query-utils.ts` wraps raw browser
 * TypeErrors as `ApiError(message, 0)`, which is how production Sentry sees
 * `ApiError: Failed to fetch` on TanStack paths (e.g. issue 7693570517 on
 * `/home`). Open PORTAL-WY / PORTAL-99 / PORTAL-WV classifiers that only
 * match `error.name === 'TypeError'` miss this wrapped shape.
 *
 * Distinct from Node/undici's `TypeError: fetch failed`, which is covered by
 * `isTransientNetworkError` for server-side best-effort paths.
 *
 * Used to keep Sentry free of transport noise from TanStack Query/Mutation
 * `onError`, client `logError` → `reportError`, and direct `captureException`
 * paths.
 */
export function isBrowserFetchTransportError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  if (error.name === 'TypeError') {
    return isBrowserFetchTransportMessage(error.message);
  }

  // apiFetch (and a few mutation wrappers) rethrow transport failures as
  // status-0 ApiErrors, preserving the original browser message.
  if (isApiError(error) && error.status === 0) {
    return isApiFetchTransportMessage(error.message);
  }

  return false;
}

const PARENTHETICAL_HOST_SUFFIX = /\s*\([^)]+\)\s*$/;

/**
 * Message-only half of {@link isBrowserFetchTransportError}. Used by Sentry
 * `beforeSend` filters that inspect event exception values rather than
 * `Error` instances.
 */
export function isBrowserFetchTransportMessage(message: string): boolean {
  const trimmed = message.trim();
  if (trimmed.startsWith('Load failed')) return true;

  const withoutHost = trimmed.replace(PARENTHETICAL_HOST_SUFFIX, '').trim();
  const lower = withoutHost.toLowerCase();
  return (
    withoutHost === 'Failed to fetch' ||
    lower === 'network error' ||
    withoutHost === 'NetworkError when attempting to fetch resource.'
  );
}

function isApiFetchTransportMessage(message: string): boolean {
  const trimmed = message.trim();
  return (
    isBrowserFetchTransportMessage(trimmed) ||
    // apiFetch timeout when the caller's signal did not cancel.
    trimmed === 'Request timed out' ||
    // apiFetch fallback when the thrown value was not an Error.
    trimmed === 'Network error'
  );
}
